/* eslint-disable react/no-danger, react/no-children-prop, react/jsx-boolean-value */
import type { FC } from 'react';
import React, { memo, useState, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightManager } from '@atlaskit/onboarding';
import { PopoverProvider } from '@atlaskit/spotlight';
import { fg } from '@confluence/feature-gating';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Blanket from '@atlaskit/blanket';
import { IsFhsEnabledProvider } from '@atlaskit/navigation-system/fhs-rollout';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { Banner } from '@atlaskit/navigation-system/layout/banner';
import { Main } from '@atlaskit/navigation-system/layout/main';
import { Root } from '@atlaskit/navigation-system/layout/root';

import { EditContentButtonPreloaderContext } from '@confluence/edit-button/entry-points/EditContentButtonPreloaderContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { PERFORMANCE_SUBJECT_navigation } from '@confluence/performance/entry-points/constants.ts';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart.ts';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { BannerContainer } from '@confluence/banner-container';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { FixedBreadcrumbsNavForPageLayoutLoader } from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import {
	CUSTOM_CONTENTS_ON_PAGE_LIST,
	SPACE_PAGES,
	SPACE_CALENDARS,
	DATABASE_CUSTOM_OVERVIEW,
	DATABASE_VIEW,
	SPACE_QUESTIONS,
	WHITEBOARD_BOARD,
} from '@confluence/named-routes/entry-points/routes.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import type { Route } from '@confluence/route/entry-points/Route.ts';
import {
	perfMark,
	WATERFALL_INITIAL_MEASURES,
} from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';
import { GlobalComponentsLoader } from '@confluence/global-components';
import { PreviewPanel } from '@confluence/preview-panel-api/entry-points/PreviewPanelContainer.ts';
import { expVal } from '@confluence/feature-experiments';
import { ObjectSidebarContent } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarContent.tsx';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { useKeyboardShortcutsEnabled } from '@confluence/shortcuts/entry-points/useKeyboardShortcutsEnabled.ts';

import { NavigationExperienceWrapper } from './NavigationExperienceWrapper.tsx';
import { SideNavigation } from './SideNavigation.tsx';
import { useScrollTree } from './useScrollTree.ts';
import { PanelLoader } from './PanelLoader.tsx';
import { i18n } from './messages.ts';
import type { PageLayoutProps } from './PageLayoutTypes.ts';
import {
	LayoutMainWrapper,
	globalStylesCompiled,
	fixSSRContentForNewLayoutWithRightSidebarStyles,
	fixSSRContentForNewLayoutWithRightSidebarStylesNew,
	SSR_LAYOUT_DATA_ATTR,
	SSR_IFRAME_ATTR,
} from './presentationComponents.tsx';
import { useSideNavInitialState } from './useSideNavInitialState.ts';
import { TopNav } from './TopNav.tsx';
import { PageLayoutContextProviderWrapper } from './PageLayoutContextProviderWrapper.tsx';

const TOP_NAV_HEIGHT = 48;

const isRoute = (pathname: string | undefined, route: Route) =>
	Boolean(route.match(pathname || ''));

const routesToShowPageLayoutBreadcrumbNav = [
	CUSTOM_CONTENTS_ON_PAGE_LIST,
	SPACE_PAGES,
	SPACE_CALENDARS,
	DATABASE_CUSTOM_OVERVIEW,
	SPACE_QUESTIONS,
];

export const PageLayout: FC<PageLayoutProps> = memo(
	({ children, view, standalone, isSpaceSettingsScreen, editorPreloader }) => {
		return (
			<ErrorBoundary attribution={Attribution.BACKBONE} attributes={outerErrorBoundaryAttributes}>
				<PageLayoutInner
					children={children}
					view={view}
					standalone={standalone}
					isSpaceSettingsScreen={isSpaceSettingsScreen}
					editorPreloader={editorPreloader}
				/>
			</ErrorBoundary>
		);
	},
);

export const PageLayoutInner: FC<PageLayoutProps> = ({
	children,
	view,
	standalone,
	isSpaceSettingsScreen = false,
	editorPreloader = noopEditorPreloader,
}) => {
	const { pageTreeUpdater } = useScrollTree({ view });
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();

	const intl = useIntl();
	const { location, match } = useContext(RoutesContext);

	// show breadcrumb nav for routes listed in routesToShowPageLayoutBreadcrumbNav
	const showBreadcrumbNavHeader = routesToShowPageLayoutBreadcrumbNav.some((route) =>
		isRoute(location?.pathname, route),
	);

	const SideNavSkiplinkText = intl.formatMessage(i18n.SideNav);

	const nonceValue = window.__SSR_NONCE_VALUE__;

	const standaloneGlobalStylesCompiled = (bannerHeight: number) =>
		standalone ? (
			/*
			 * We're injecting global styles to handle styling targets that are not part of the React app, but are children of the HTML body
			 * Previously, this used to be a `<Global styles={[…]} />` from Emotion
			 * But in the Compiled migration, there is no alternative and feels like the best path forward for now.
			 */
			<>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
				<style
					type="text/css"
					dangerouslySetInnerHTML={{
						__html: fg('platform_dst_nav4_top_nav_increase_height')
							? fixSSRContentForNewLayoutWithRightSidebarStylesNew
							: fixSSRContentForNewLayoutWithRightSidebarStyles,
					}}
					data-testid="global-styles-SSR"
				/>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
				<style
					{...(nonceValue !== undefined ? { nonce: nonceValue } : {})}
					type="text/css"
					dangerouslySetInnerHTML={{ __html: globalStylesCompiled }}
				/>
				{fg('platform_dst_nav4_top_nav_increase_height') ? null : (
					/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */
					<style type="text/css">{`[${SSR_LAYOUT_DATA_ATTR}], [${SSR_IFRAME_ATTR}] {
						top: ${bannerHeight + TOP_NAV_HEIGHT}px !important;
					}`}</style>
				)}
			</>
		) : (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
			<style
				data-testid="global-styles"
				{...(nonceValue !== undefined ? { nonce: nonceValue } : {})}
				type="text/css"
				dangerouslySetInnerHTML={{ __html: globalStylesCompiled }}
			/>
		);

	const [isNav4CollapseReminderSpotlightActive, setIsNav4CollapseReminderSpotlightActive] =
		useState<boolean>(false);
	const { initialCollapsedState } = useSideNavInitialState();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const panel = usePanel();
	const isContentRoute = Boolean(match?.params?.contentId);

	useEffect(() => {
		if (
			isObjectSidebarShown &&
			isMigratedToPanelSystem(panel?.id) &&
			!isContentRoute &&
			fg('ads-panel-system-confluence-object-sidebar')
		) {
			void hideObjectSidebar();
		}
	}, [hideObjectSidebar, isContentRoute, isObjectSidebarShown, panel?.id]);

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE} attributes={innerErrorBoundaryAttributes}>
			<NavigationExperienceWrapper view={view} navVersion="4">
				<PerformanceStart subject={PERFORMANCE_SUBJECT_navigation} subjectId="NavigationLoading" />
				<EditContentButtonPreloaderContext.Provider value={editorPreloader}>
					<Subscribe to={bannerStateContainerArray}>
						{(bannerState: BannerStateContainer) => {
							const bannerHeight = bannerState.getTotalHeight(false);

							const SpotlightWrapper = fg('confluence_frontend_spotlight_ads_migration')
								? ({ children: childrenProp }: { children: React.ReactNode }) => (
										<PopoverProvider>{childrenProp}</PopoverProvider>
									)
								: ({ children: childrenProp }: { children: React.ReactNode }) => (
										<SpotlightManager blanketIsTinted={true}>{childrenProp}</SpotlightManager>
									);

							return (
								<SpotlightWrapper>
									{standaloneGlobalStylesCompiled(bannerHeight)}
									<PageLayoutContextProviderWrapper>
										<IsFhsEnabledProvider
											value={expVal('navx-full-height-sidebar-exp', 'isEnabled', false)}
										>
											<Root
												UNSAFE_dangerouslyHoistSlotSizes
												testId="page-layout-root"
												defaultSideNavCollapsed={
													expVal('navx-full-height-sidebar-exp', 'isEnabled', false)
														? initialCollapsedState === 'collapsed'
														: undefined
												}
												isSideNavShortcutEnabled={
													areKeyboardShortcutsEnabled &&
													expVal('navx-full-height-sidebar-exp', 'isEnabled', false)
												}
											>
												{/*<ZIndexWrapper zIndex={14}>*/}
												<Banner height={bannerHeight} id="AkBanner">
													<BannerContainer />
												</Banner>
												<SuspenseWithDataFetcher dataFetcherName="top-nav">
													{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_TOP_NAV}.start`)}
													<TopNav
														defaultCollapsed={initialCollapsedState === 'collapsed'}
														isNav4CollapseReminderSpotlightActive={
															isNav4CollapseReminderSpotlightActive
														}
														setIsNav4CollapseReminderSpotlightActive={
															setIsNav4CollapseReminderSpotlightActive
														}
													/>
													{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_TOP_NAV}.end`)}
												</SuspenseWithDataFetcher>

												{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_SIDE_NAV}.start`)}
												<SideNavigation
													view={view}
													skiplinkText={SideNavSkiplinkText}
													isSpaceSettingsScreen={isSpaceSettingsScreen}
													isNav4CollapseReminderSpotlightActive={
														isNav4CollapseReminderSpotlightActive
													}
													setIsNav4CollapseReminderSpotlightActive={
														setIsNav4CollapseReminderSpotlightActive
													}
													setTriggeredByShortcut={() => {}}
												/>
												{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_SIDE_NAV}.end`)}
												{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_MAIN}.start`)}
												<Main testId="grid-main-container" id="AkMainContent">
													<ContentTreeMultiSelectOverlay />
													<LayoutMainWrapper
														id="layout-main-wrapper"
														showBreadcrumbNavHeader={showBreadcrumbNavHeader}
													>
														{showBreadcrumbNavHeader && <FixedBreadcrumbsNavForPageLayoutLoader />}

														{children && !standalone ? (
															<ErrorBoundary attribution={Attribution.DISCO}>
																{showBreadcrumbNavHeader ? (
																	<Box xcss={breadcrumbsOffsetStyles}>{children}</Box>
																) : (
																	children
																)}
															</ErrorBoundary>
														) : null}
														{perfMark(`${WATERFALL_INITIAL_MEASURES.HTML_MAIN}.end`)}
													</LayoutMainWrapper>
												</Main>
												{isObjectSidebarShown &&
													isMigratedToPanelSystem(panel?.id) &&
													isContentRoute &&
													!isRoute(location?.pathname, WHITEBOARD_BOARD) &&
													!isRoute(location?.pathname, DATABASE_VIEW) &&
													fg('ads-panel-system-confluence-object-sidebar') && (
														<Panel defaultWidth={400} label="Object Sidebar">
															<ObjectSidebarContent />
														</Panel>
													)}
												<PanelLoader toggleInProductHelp={() => {}} />
												<Subscribe to={[PageTreeStateUpdater]}>
													{(pageTreeStateUpdatesContainer: PageTreeStateUpdater) => {
														pageTreeUpdater.current = pageTreeStateUpdatesContainer;
														return null;
													}}
												</Subscribe>
												<PreviewPanel />
											</Root>
											<GlobalComponentsLoader />
										</IsFhsEnabledProvider>
									</PageLayoutContextProviderWrapper>
								</SpotlightWrapper>
							);
						}}
					</Subscribe>
				</EditContentButtonPreloaderContext.Provider>
			</NavigationExperienceWrapper>
		</ErrorBoundary>
	);
};

const ContentTreeMultiSelectOverlay = () => {
	const [isMultiSelectActive] = useIsMultiSelectActive();
	return isMultiSelectActive ? (
		<Blanket isTinted testId="content-tree-multi-select-blanket" />
	) : null;
};

const breadcrumbsOffsetStyles = xcss({
	// @ts-ignore This specific value is necessary to ensure the breadcrumbs are offset correctly
	marginTop: '60px',
});

const outerErrorBoundaryAttributes = {
	errorBoundaryId: 'PageLayout-outer',
};

const innerErrorBoundaryAttributes = {
	errorBoundaryId: 'PageLayoutInner',
};

const bannerStateContainerArray = [BannerStateContainer];

const noopEditorPreloader: NonNullable<PageLayoutProps['editorPreloader']> = () => null;
